/**
 * NovaCAD entry point.
 *
 * Boot: detect graphics → viewport → document (JS geometry kernel, seeded on
 * first run) → App shell (selection, tools, keyboard) → UI panels.
 */

import { App } from './app';
import { startMcpBridge } from './ai/mcpBridge';
import { CadDocument } from '@core/document';
import { BrepKernel } from '@core/geometry/brepKernel';
import { requestPersistentStorage } from '@core/storage/db';
import { loadDefaultFont } from '@core/text/fontCache';
import { ref, type FeatureId } from '@core/timeline/types';
import { detectGraphics } from '@render/capabilities';
import { Viewport } from '@render/viewport';
import { DimInput } from '@ui/dimInput';
import { mountAiPanel } from '@ui/aiPanel';
import { mountAssistantPanel } from '@ui/assistantPanel';
import { mountAssetsPanel } from '@ui/assetsPanel';
import { mountBodiesPanel } from '@ui/bodiesPanel';
import { mountProjectsPanel } from '@ui/projectsPanel';
import { getSetting } from '@core/storage/settings';
import { mountCheatsheet } from '@ui/cheatsheet';
import { mountDrawingPanel } from '@ui/drawingPanel';
import { mountHud } from '@ui/hud';
import { mountParamsPanel } from '@ui/paramsPanel';
import { mountPropertiesPanel } from '@ui/propertiesPanel';
import { mountSectionPanel } from '@ui/sectionPanel';
import { injectStyles } from '@ui/styles';
import { mountTimelinePanel } from '@ui/timelinePanel';
import { mountToolbar } from '@ui/toolbar';

const bootStatus = (msg: string): void => {
  const el = document.getElementById('boot-status');
  if (el) el.textContent = msg;
};

// Offline/PWA: cache-first for immutable assets + the CDN kernel, so after
// the first visit NovaCAD works with no network at all.
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => undefined);
  });
}

/**
 * First-run showcase: a parametric clamp block that demonstrates the
 * pillars in one part — named parameters driving an expression-based
 * thickness and hole size, a face-attached boss that rides the plate,
 * counterbored holes from the hole wizard, and a chamfered rim. Open the
 * ƒx panel and change `plateH` or `boltD` to watch everything regenerate.
 */
async function seedDemo(doc: CadDocument): Promise<void> {
  await doc.apply('Parameters', (t) =>
    t.setParam('bossH', '22').setParam('boltD', '5.5'),
  );

  let plateSketch!: FeatureId;
  await doc.apply('Base plate sketch', (t) => {
    const { timeline, feature } = t.addFeature('sketch', 'Base sketch', {
      plane: { kind: 'datum', plane: 'XY', offset: 0 },
      entities: [{ kind: 'rect', min: [-45, -30], max: [45, 30] }],
    });
    plateSketch = feature.id;
    return timeline;
  });
  await doc.apply('Base plate', (t) =>
    t.addFeature('extrude', 'Base plate', {
      profile: ref(plateSketch, 'profile:0'),
      distance: 12,
      op: 'new-body',
    }).timeline,
  );
  const plate = doc.timeline.features[doc.timeline.features.length - 1]!.id;

  let bossSketch!: FeatureId;
  await doc.apply('Boss sketch', (t) => {
    const { timeline, feature } = t.addFeature('sketch', 'Boss sketch', {
      // Face-attached: the boss rides the plate top through any edit.
      plane: { kind: 'face', face: ref(plate, 'face:top') },
      entities: [
        { kind: 'circle', center: [0, 0], radius: 18 },
        { kind: 'circle', center: [0, 0], radius: 8 },
      ],
    });
    bossSketch = feature.id;
    return timeline;
  });
  await doc.apply('Boss', (t) =>
    t.addFeature('extrude', 'Boss', {
      profile: ref(bossSketch, 'profile:0'),
      distance: { $expr: 'bossH' } as never,
      op: 'new-body',
    }).timeline,
  );
  const boss = doc.timeline.features[doc.timeline.features.length - 1]!.id;

  await doc.apply('Boss chamfer', (t) =>
    t.addFeature('chamfer', 'Boss chamfer', {
      target: ref(boss),
      distance: 1.5,
    }).timeline,
  );

  await doc.apply('Bolt holes', (t) =>
    t.addFeature('hole', 'Bolt holes', {
      target: ref(plate),
      holes: ([[-36, -21], [36, -21], [36, 21], [-36, 21]] as const).map(([x, y]) => ({
        point: [x, y, 12] as [number, number, number],
        normal: [0, 0, 1] as [number, number, number],
      })),
      diameter: { $expr: 'boltD' } as never,
      depth: 'through',
      style: 'counterbore',
      cbDiameter: 10,
      cbDepth: 3.5,
    }).timeline,
  );
}

async function main(): Promise<void> {
  bootStatus('Detecting graphics backend…');
  const gfx = await detectGraphics();
  if (gfx.backend === 'none') {
    bootStatus('This browser supports neither WebGPU nor WebGL 2 — NovaCAD cannot run.');
    return;
  }

  injectStyles();
  const host = document.getElementById('viewport-host')!;
  bootStatus('Starting renderer…');
  const viewport = await Viewport.create(host, gfx);

  const persisted = await requestPersistentStorage();
  // The real B-rep kernel (OpenCascade in a worker); degrades to the JS mesh
  // kernel automatically if the WASM cannot load.
  const kernel = new BrepKernel();
  kernel.warmup();
  // Sketch-text font (bundled Inter) — non-blocking; text features error
  // gracefully and recover if someone races the first load.
  void loadDefaultFont().catch(() => undefined);

  // Reopen the last project; fall back to the legacy 'demo' id; else first run.
  const lastId = await getSetting<string | null>('lastProject', null);
  let doc = lastId ? await CadDocument.open(lastId, kernel) : undefined;
  doc ??= await CadDocument.open('demo', kernel);
  const firstRun = !doc;
  doc ??= new CadDocument('demo', 'Showcase', kernel);

  const dim = new DimInput(host);
  const app = new App(doc, kernel, viewport, dim);

  mountToolbar(host, app);
  mountPropertiesPanel(host, app);
  mountTimelinePanel(document.getElementById('timeline-host')!, app);
  mountAiPanel(host, app);
  mountAssetsPanel(host, app);
  mountBodiesPanel(host, app);
  mountProjectsPanel(host, app);
  mountAssistantPanel(host, app);
  mountParamsPanel(host, app);
  mountSectionPanel(host, app);
  mountCheatsheet(host, app);
  mountDrawingPanel(host, app);

  if (firstRun) {
    await seedDemo(doc);
  } else {
    await doc.refresh();
  }
  viewport.fitAll();

  // The autosave debounce (500ms) would lose the newest edit if the tab
  // closes/reloads inside that window — flush on the way out. (pagehide is
  // the reliable signal on Safari; IndexedDB writes started here complete.)
  window.addEventListener('pagehide', () => void app.doc.flushSave());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void app.doc.flushSave();
  });

  // Announce which geometry kernel we ended up with.
  const announceKernel = (mode: 'brep' | 'mesh'): void =>
    app.setIoStatus(
      mode === 'brep'
        ? 'B-rep kernel active — exact OpenCascade geometry, STEP export, fillets'
        : 'B-rep kernel unavailable — running on the mesh kernel',
    );
  if (kernel.mode !== 'pending') announceKernel(kernel.mode);
  else kernel.onReady = announceKernel;

  // MCP: connect to a local mcp/server.mjs hub when one is running, so
  // external agents (Claude Code/Desktop) can drive this document.
  let mcpWasConnected = false;
  startMcpBridge(app.toolbox, ({ connected }) => {
    if (connected && !mcpWasConnected) {
      app.setIoStatus('MCP connected — external agents can drive this document');
    }
    mcpWasConnected = connected;
  });

  // Debug/console handle (also used by automated smoke checks).
  (window as unknown as Record<string, unknown>).__novacad = { viewport, doc, app };

  document.getElementById('boot')?.remove();
  mountHud(host, {
    backend: viewport.backend,
    aoEnabled: viewport.aoEnabled,
    persistedStorage: persisted,
  });
}

main().catch((err) => {
  console.error(err);
  bootStatus(`Boot failed: ${err instanceof Error ? err.message : String(err)}`);
});
